import React, { ReactElement, useState } from "react";
import { useIntl } from "react-intl";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import useMediaQuery from "@mui/material/useMediaQuery";

import { IAllAddonsAPI, IAllAddonsDataset } from "types/api/addonsApiInterface";
import { addonsSectionType, addonsTeamsPermissionType } from "types/global/teams";
import { addonsPermissionType, addonsSelectedList } from "types/global/user";

import TeamsCloudbox from "./addonsSettingSections/TeamsCloudbox";
import TeamsCloudbucket from "./addonsSettingSections/TeamsCloudbucket";
import TeamsFirewall from "./addonsSettingSections/TeamsFirewall";
import TeamsFloatingIp from "./addonsSettingSections/TeamsFloatingIp";
import TeamsLoadBalancer from "./addonsSettingSections/TeamsLoadBalancer";
import TeamsNetwork from "./addonsSettingSections/TeamsNetwork";
import TeamsSnapshot from "./addonsSettingSections/TeamsSnapshot";
import TeamsVolumes from "./addonsSettingSections/TeamsVolumes";

const AddonsSection = ({
 control,
 addons,
 selectedAddons,
 addonsPermissions,
 setAddons,
 addAddons,
 setPermission
}: {
 control: any;
 addons: IAllAddonsAPI | null;
 selectedAddons: addonsSelectedList;
 addonsPermissions: addonsPermissionType;
 setAddons: (section: addonsSectionType, value: Array<number>) => void;
 addAddons: (section: addonsSectionType, value: Array<{ id: number; label: string }>) => void;
 setPermission: (section: addonsTeamsPermissionType, value: boolean) => void;
}): ReactElement => {
 const intl = useIntl();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("md"));

 const [section, setSection] = useState<number>(0);
 const [allCloudbox, setAllCloudbox] = useState<boolean>(false);
 const [allVolumes, setAllVolumes] = useState<boolean>(false);
 const [allFloatingips, setAllFloatingips] = useState<boolean>(false);
 const [allNetworks, setAllNetworks] = useState<boolean>(false);
 const [allSnapshots, setAllSnapshots] = useState<boolean>(false);
 const [allFirewalls, setAllFirewalls] = useState<boolean>(false);
 const [allBalancers, setAllBalancers] = useState<boolean>(false);
 const [allBuckets, setAllBuckets] = useState<boolean>(false);

 const addAllAddons = (
  section: addonsSectionType,
  sectionArray: Array<number>,
  sectionData: Array<IAllAddonsDataset>
 ): void => {
  let tempData: Array<number> = sectionArray;
  sectionData
   .filter((element) => !sectionArray.includes(element.id))
   .map((element) => tempData.push(element.id));
  setAddons(section, tempData);
 };

 const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
  setSection(newValue);
 };

 const RenderTabContent = (): ReactElement => {
  switch (section) {
   case 0:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allCloudbox}
         onChange={(e, checked) => {
          setAllCloudbox(checked);
          if (checked)
           addAllAddons("cloudboxes", selectedAddons.cloudboxes, addons?.cloudboxes || []);
          else setAddons("cloudboxes", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllCloudboxes" })}
      />
      <TeamsCloudbox
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allCloudbox}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 1:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allVolumes}
         onChange={(e, checked) => {
          setAllVolumes(checked);
          if (checked) addAllAddons("volumes", selectedAddons.volumes, addons?.volumes || []);
          else setAddons("volumes", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllVolumes" })}
      />
      <TeamsVolumes
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allVolumes}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 2:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allFloatingips}
         onChange={(e, checked) => {
          setAllFloatingips(checked);
          if (checked)
           addAllAddons("floating_ips", selectedAddons.floating_ips, addons?.floating_ips || []);
          else setAddons("floating_ips", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllFloatingips" })}
      />
      <TeamsFloatingIp
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allFloatingips}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 3:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allNetworks}
         onChange={(e, checked) => {
          setAllNetworks(checked);
          if (checked) addAllAddons("networks", selectedAddons.networks, addons?.networks || []);
          else setAddons("networks", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllNetworks" })}
      />
      <TeamsNetwork
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allNetworks}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 4:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allSnapshots}
         onChange={(e, checked) => {
          setAllSnapshots(checked);
          if (checked) addAllAddons("snapshots", selectedAddons.snapshots, addons?.snapshots || []);
          else setAddons("snapshots", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllSnapshots" })}
      />
      <TeamsSnapshot
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allSnapshots}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 5:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allFirewalls}
         onChange={(e, checked) => {
          setAllFirewalls(checked);
          if (checked) addAllAddons("firewalls", selectedAddons.firewalls, addons?.firewalls || []);
          else setAddons("firewalls", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllFirewalls" })}
      />
      <TeamsFirewall
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allFirewalls}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 6:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allBalancers}
         onChange={(e, checked) => {
          setAllBalancers(checked);
          if (checked)
           addAllAddons(
            "load_balancers",
            selectedAddons.load_balancers,
            addons?.load_balancers || []
           );
          else setAddons("load_balancers", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllLoadBalancers" })}
      />
      <TeamsLoadBalancer
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allBalancers}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   case 7:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allBuckets}
         onChange={(e, checked) => {
          setAllBuckets(checked);
          if (checked)
           addAllAddons("cloudbuckets", selectedAddons.cloudbuckets, addons?.cloudbuckets || []);
          else setAddons("cloudbuckets", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllCloudbuckets" })}
      />
      <TeamsCloudbucket
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       disabledComplete={allBuckets}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
      />
     </Stack>
    );
   default:
    return (
     <Stack spacing={2}>
      <FormControlLabel
       control={
        <Checkbox
         checked={allCloudbox}
         onChange={(e, checked) => {
          setAllCloudbox(checked);
          if (checked)
           addAllAddons("cloudboxes", selectedAddons.cloudboxes, addons?.cloudboxes || []);
          else setAddons("cloudboxes", []);
         }}
        />
       }
       label={intl.formatMessage({ id: "teams.usersettings.selectAllCloudboxes" })}
      />
      <TeamsCloudbox
       control={control}
       addons={addons}
       selectedAddons={selectedAddons}
       addonsPermissions={addonsPermissions}
       setAddons={setAddons}
       addAddons={addAddons}
       setPermission={setPermission}
       disabledComplete={allCloudbox}
      />
     </Stack>
    );
  }
 };

 return (
  <Accordion>
   <AccordionSummary expandIcon={<ExpandMoreIcon />}>Addons</AccordionSummary>
   <AccordionDetails>
    <Tabs
     value={section}
     onChange={handleChangeTab}
     variant={desktopViewPort ? "standard" : "scrollable"}
     scrollButtons={true}
     allowScrollButtonsMobile
     sx={{ mb: 2 }}>
     <Tab label={intl.formatMessage({ id: "app.cloudbox" })} />
     <Tab label={intl.formatMessage({ id: "app.volumes" })} />
     <Tab label={intl.formatMessage({ id: "app.floatingIp" })} />
     <Tab label={intl.formatMessage({ id: "app.networks" })} />
     <Tab label={intl.formatMessage({ id: "app.snapshots" })} />
     <Tab label={intl.formatMessage({ id: "app.firewall" })} />
     <Tab label={intl.formatMessage({ id: "app.loadBalancer" })} />
     <Tab label={intl.formatMessage({ id: "app.cloudbuckets" })} />
    </Tabs>
    <RenderTabContent />
   </AccordionDetails>
  </Accordion>
 );
};

export default AddonsSection;
